/**
 * Warranty Calculator
 * 
 * The percentage of coverage are based on age and mileage at time of repair as set forth below:
 * |In-Service Date |Up to 36k miles |36001-50k miles |50001-72k miles |72001-80k       |
 * |----------------|----------------|----------------|----------------|----------------|
 * |<=3 years       |100%            |100%            |70%             |60%             |
 * |3-4 years       |100%            |100%            |65%             |55%             |
 * |4-5 years       |100%            |100%            |60%             |50%             |
 * |5-6 years       |100%            |100%            |55%             |45%             |
 * |>6 years        |0%              |0%              |0%              |0%              |
 * 
 * @description This function calculates the warranty status and percentage based on the in-service date, today's date, and mileage.
 * @param {string} inServiceDate - The in-service date of the vehicle in YYYY-MM-DD format.
 * @param {string} todaysDate - The current date in YYYY-MM-DD format.
 * @param {number} miles - The mileage of the vehicle.
 * @returns {object} - An object containing the warranty status and percentage.
 * @example
 * const result = warrantyCalculator("2023-05-01", "2025-05-01", 80000);
 * console.log(result); // { warrantyStatus: 'Warranty valid (3 years or less)', warrantyPercentage: 100 }
 * 
 */

function normalizeMiles(miles) {
    if (typeof miles === "number") {
        return miles;
    }

    if (typeof miles === "string") {
        const normalized = miles.replace(/,/g, "").trim();
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : NaN;
    }

    return NaN;
}

export const warrantyCalculator = (inServiceDate, todaysDate, miles) => {
    const normalizedMiles = normalizeMiles(miles);

    const serviceDate = new Date(inServiceDate).getTime(); // Convert inServiceDate to milliseconds
    const currentDate = new Date(todaysDate).getTime(); // Convert todaysDate to milliseconds
    const timeDifference = currentDate - serviceDate; // difference in milliseconds
    const yearsDifference = timeDifference / (1000 * 60 * 60 * 24 * 365); // Convert milliseconds to years

    let warrantyStatus = '';
    let warrantyPercentage = 0;
    let row;
    let column;

    if (
        !Number.isFinite(serviceDate) ||
        !Number.isFinite(currentDate) ||
        currentDate < serviceDate
    ) {
        return {
            warrantyStatus: "Expired",
            warrantyPercentage: 0
        };
    }

    if (!Number.isFinite(normalizedMiles) || normalizedMiles > 80000) {
        return {
            warrantyStatus: 'Expired',
            warrantyPercentage: 0
        }
    }

    // Calculate warranty percentage based on years and miles
    if (yearsDifference <= 3) {
        // warrantyStatus = 'Warranty valid (3 years or less)';
        warrantyStatus = 'Valid';
        row = 1;

        if (normalizedMiles <= 36000) {
            warrantyPercentage = 100;
            column = 1;
        } else if (normalizedMiles <= 50000) {
            warrantyPercentage = 100;
            column = 2;
        } else if (normalizedMiles <= 72000) {
            warrantyPercentage = 70;
            column = 3;
        } else if (normalizedMiles <= 80000) {
            warrantyPercentage = 60;
            column = 4;
        }

        return {
            warrantyStatus,
            warrantyPercentage,
            row,
            column
        }
    }

    if (yearsDifference > 3 && yearsDifference <= 4) {
        warrantyStatus = 'Valid';
        row = 2;
        if (normalizedMiles <= 36000) {
            warrantyPercentage = 100;
            column = 1;
        } else if (normalizedMiles <= 50000) {
            warrantyPercentage = 100;
            column = 2;
        } else if (normalizedMiles <= 72000) {
            warrantyPercentage = 65;
            column = 3;
        } else if (normalizedMiles <= 80000) {
            warrantyPercentage = 55;
            column = 4;
        }

        return {
            warrantyStatus,
            warrantyPercentage,
            row,
            column
        }

    }

    if (yearsDifference > 4 && yearsDifference <= 5) {
        warrantyStatus = 'Valid';
        row = 3;
        if (normalizedMiles <= 36000) {
            warrantyPercentage = 80;
            column = 1;
        } else if (normalizedMiles <= 50000) {
            warrantyPercentage = 70;
            column = 2;
        } else if (normalizedMiles <= 72000) {
            warrantyPercentage = 60;
            column = 3;
        } else if (normalizedMiles <= 80000) {
            warrantyPercentage = 50;
            column = 4;
        }

        return {
            warrantyStatus,
            warrantyPercentage,
            row,
            column
        }
    }

    if (yearsDifference > 5 && yearsDifference <= 6) {
        warrantyStatus = 'Valid';
        row = 4;
        if (normalizedMiles <= 36000) {
            warrantyPercentage = 75;
            column = 1;
        } else if (normalizedMiles <= 50000) {
            warrantyPercentage = 65;
            column = 2;
        } else if (normalizedMiles <= 72000) {
            warrantyPercentage = 55;
            column = 3;
        } else if (normalizedMiles <= 80000) {
            warrantyPercentage = 45;
            column = 4;
        }

        return {
            warrantyStatus,
            warrantyPercentage,
            row,
            column
        }
    }

    if (yearsDifference > 6) {
        warrantyStatus = 'Expired';
        row = 5;
        if (normalizedMiles <= 36000) {
            warrantyPercentage = 0;
            column = 1;
        } else if (normalizedMiles <= 50000) {
            warrantyPercentage = 0;
            column = 2;
        } else if (normalizedMiles <= 72000) {
            warrantyPercentage = 0;
            column = 3;
        } else if (normalizedMiles <= 80000) {
            warrantyPercentage = 0;
            column = 4;
        }

        return {
            warrantyStatus,
            warrantyPercentage,
            row,
            column
        }
    }

    return {
        warrantyStatus: 'Expired',
        warrantyPercentage: 0
    };
};
